import { ReactNode, useState } from 'react';
import {
  Result,
  postUpgradeModems,
  postUpgradeDevices,
} from '../../api/api';
import { Button } from '../Common/Button';
import { ConfirmationModal } from '../Common/ConfirmationModal';
import { BarLoader } from 'react-spinners';

interface ServiceBoxProps {
  header: string;
  description: string;
  children: ReactNode;
}

interface ServiceBoxPropsList {
  children: ReactNode;
}

const ServiceBox = ({
  header,
  description,
  children,
}: ServiceBoxProps) => {
  return (
    <div className="max-w-xlg rounded overflow-hidden shadow-lg px-8 py-8">
      <div className="font-bold text-xl mb-2">{header}</div>
      <p className="text-gray-700 text-base mt-2">{description}</p>
      {children}
    </div>
  );
};

const ServiceBoxList = ({ children }: ServiceBoxPropsList) => {
  return (
    <div className="flex justify-start mx-4 my-4">{children}</div>
  );
};

export const ServicesOverview = () => {
  const [devicesUpgradedResult, setDevicesUpgradedResult] = useState<
    Result<string>
  >({ status: '' });
  const [loadingDeviceUpgrade, setloadingDeviceUpgrade] =
    useState<boolean>(false);
  const [isDeviceConfirmationOpen, setIsDeviceConfirmationOpen] =
    useState(false);
  const [modemsUpgradedResult, setModemsUpgradedResult] = useState<
    Result<string>
  >({ status: '' });
  const [loadingModemUpgrade, setloadingModemUpgrade] =
    useState<boolean>(false);
  const [isModemConfirmationOpen, setIsModemConfirmationOpen] =
    useState(false);

  const setDevicesUpgraded = async () => {
    if (loadingDeviceUpgrade) return;

    setloadingDeviceUpgrade(true);
    const result = await postUpgradeDevices();
    setloadingDeviceUpgrade(false);
    setDevicesUpgradedResult(result);
    if (result.status === 'OK') {
      setIsDeviceConfirmationOpen(false);
    }
  };

  const setModemsUpgraded = async () => {
    if (loadingModemUpgrade) return;

    setloadingModemUpgrade(true);
    const result = await postUpgradeModems();
    setloadingModemUpgrade(false);
    setModemsUpgradedResult(result);
    if (result.status === 'OK') {
      setIsModemConfirmationOpen(false);
    }
  };

  return (
    <>
      <ServiceBoxList>
        <ServiceBox
          header="Upgrade"
          description="Perform batch upgrade for devices or modems"
        >
          <div className="grid grid-cols-2 gap-3 flex items-center my-5">
            <Button
              text="Upgrade devices"
              onClick={() => {
                setDevicesUpgradedResult({ status: '' });
                setIsDeviceConfirmationOpen(true);
              }}
              disabled={[
                {
                  disabled:
                    loadingDeviceUpgrade ||
                    devicesUpgradedResult.status === 'OK',
                  reason: loadingDeviceUpgrade
                    ? 'Device upgrades are being submitted.'
                    : 'Device upgrades have already been submitted.',
                },
              ]}
            />
            {loadingDeviceUpgrade ? (
              <BarLoader color="#4B5058" />
            ) : (
              <ServiceResult result={devicesUpgradedResult} />
            )}

            <Button
              text="Upgrade modems"
              onClick={() => {
                setModemsUpgradedResult({ status: '' });
                setIsModemConfirmationOpen(true);
              }}
              disabled={[
                {
                  disabled:
                    loadingModemUpgrade ||
                    modemsUpgradedResult.status === 'OK',
                  reason: loadingModemUpgrade
                    ? 'Modem upgrades are being submitted.'
                    : 'Modem upgrades have already been submitted.',
                },
              ]}
            />
            {loadingModemUpgrade ? (
              <BarLoader color="#4B5058" />
            ) : (
              <ServiceResult result={modemsUpgradedResult} />
            )}
          </div>
        </ServiceBox>
      </ServiceBoxList>
      <ConfirmationModal
        isOpen={isDeviceConfirmationOpen}
        title="Upgrade devices"
        message="This will attempt a firmware upgrade for all configured products and their eligible devices. The operation may affect many devices."
        confirmText="Upgrade devices"
        pending={loadingDeviceUpgrade}
        pendingText="Submitting..."
        errorMessage={getResultErrorMessage(devicesUpgradedResult)}
        onConfirm={setDevicesUpgraded}
        onCancel={() => {
          if (!loadingDeviceUpgrade) {
            setIsDeviceConfirmationOpen(false);
          }
        }}
      />
      <ConfirmationModal
        isOpen={isModemConfirmationOpen}
        title="Upgrade modems"
        message="This will attempt modem firmware upgrades for all devices with pending modem firmware upgrades."
        confirmText="Upgrade modems"
        pending={loadingModemUpgrade}
        pendingText="Submitting..."
        errorMessage={getResultErrorMessage(modemsUpgradedResult)}
        onConfirm={setModemsUpgraded}
        onCancel={() => {
          if (!loadingModemUpgrade) {
            setIsModemConfirmationOpen(false);
          }
        }}
      />
    </>
  );
};

const ServiceResult = ({ result }: { result: Result<string> }) => {
  if (result.status === 'OK') {
    return <p role="status">{result.data}</p>;
  }

  const errorMessage = getResultErrorMessage(result);
  return errorMessage ? <p role="alert">{errorMessage}</p> : null;
};

function getResultErrorMessage(
  result: Result<string>,
): string | undefined {
  if (
    result.status === 'Error' ||
    result.status === 'Not Found' ||
    result.status === 'Network issue' ||
    result.status === 'Forbidden'
  ) {
    return `${result.status}: ${result.errorMessages.join(', ')}`;
  }

  return undefined;
}

export default ServicesOverview;
